import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Sparkles, ExternalLink, Loader2, CheckCircle2, AlertCircle, ArrowLeft, Presentation, FileUp, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './DeckBuilder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Steps
const STEPS = {
  UPLOAD: 'upload',
  PREVIEW: 'preview',
  GENERATING: 'generating',
  COMPLETE: 'complete'
};

export default function DeckBuilderPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  
  // File data
  const [fileData, setFileData] = useState(null);
  const [customTitle, setCustomTitle] = useState('');
  
  // Generation data
  const [generationId, setGenerationId] = useState(null);
  const [gammaUrl, setGammaUrl] = useState(null);
  const [credits, setCredits] = useState(null);

  // Handle file selection
  const handleFile = async (file) => {
    setError(null);
    
    if (!file) return;
    
    // Validate file type
    const validTypes = ['text/plain', 'text/markdown', 'text/x-markdown'];
    const validExtensions = ['.txt', '.md'];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!validTypes.includes(file.type) && !hasValidExtension) {
      setError('Solo se permiten archivos .txt o .md');
      return;
    }
    
    try {
      const content = await file.text();
      
      if (!content.trim()) {
        setError('El archivo está vacío');
        return;
      }
      
      // Send to backend for processing
      const response = await fetch(`${BACKEND_URL}/api/deck/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          file_content: content,
          title: file.name.replace(/\.(txt|md)$/i, '')
        })
      });
      
      if (!response.ok) {
        throw new Error('Error al procesar el archivo');
      }
      
      const data = await response.json();
      setFileData({
        name: file.name,
        ...data
      });
      setCustomTitle(data.detected_title || '');
      setCurrentStep(STEPS.PREVIEW);
      
    } catch (err) {
      setError(err.message || 'Error al leer el archivo');
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  // Generate presentation
  const handleGenerate = async () => {
    if (!fileData?.full_content) return;
    
    setError(null);
    setCurrentStep(STEPS.GENERATING);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/deck/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: fileData.full_content,
          title: customTitle || fileData.detected_title
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error al generar la presentación');
      }
      
      const data = await response.json();
      setGenerationId(data.generation_id);
      
      // If already complete
      if (data.status === 'completed' && data.gamma_url) {
        setGammaUrl(data.gamma_url);
        setCredits(data.credits);
        setCurrentStep(STEPS.COMPLETE);
        return;
      }
      
      // Poll for status
      pollGenerationStatus(data.generation_id);
      
    } catch (err) {
      setError(err.message);
      setCurrentStep(STEPS.PREVIEW);
    }
  };

  // Poll for generation status
  const pollGenerationStatus = async (genId) => {
    const maxAttempts = 60; // 2 minutes max
    let attempts = 0;
    
    const poll = async () => {
      attempts++;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/deck/status/${genId}`);
        
        if (!response.ok) {
          throw new Error('Error al verificar estado');
        }
        
        const data = await response.json();
        
        if (data.status === 'completed' && data.gamma_url) {
          setGammaUrl(data.gamma_url);
          setCredits(data.credits);
          setCurrentStep(STEPS.COMPLETE);
          return;
        }
        
        if (data.status === 'failed') {
          throw new Error('La generación falló');
        }
        
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else {
          throw new Error('Tiempo de espera agotado');
        }
        
      } catch (err) {
        setError(err.message);
        setCurrentStep(STEPS.PREVIEW);
      }
    };
    
    poll();
  };

  // Reset to start
  const handleReset = () => {
    setCurrentStep(STEPS.UPLOAD);
    setFileData(null);
    setCustomTitle('');
    setGenerationId(null);
    setGammaUrl(null);
    setCredits(null);
    setError(null);
  };

  return (
    <div className="deck-builder-page">
      {/* Header */}
      <header className="deck-header">
        <button className="deck-back-btn" onClick={() => navigate('/')}>
          <ArrowLeft size={18} />
          <span>LDM Labs</span>
        </button>
        <h1>
          <Presentation size={24} />
          Deck Builder
        </h1>
        <div className="deck-powered-by">
          Powered by <span>Gamma</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="deck-main">
        <AnimatePresence mode="wait">
          {/* Step 1: Upload */}
          {currentStep === STEPS.UPLOAD && (
            <motion.div
              key="upload"
              className="deck-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="deck-step-header">
                <FileUp size={32} />
                <h2>Sube tu archivo</h2>
                <p>Arrastra un archivo .txt o .md con el contenido de tu presentación</p>
              </div>

              <div
                className={`deck-dropzone ${dragActive ? 'active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,text/plain,text/markdown"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
                <Upload size={48} className="deck-dropzone-icon" />
                <span className="deck-dropzone-text">
                  {dragActive ? 'Suelta el archivo aquí' : 'Haz clic o arrastra un archivo'}
                </span>
                <span className="deck-dropzone-hint">
                  Formatos: .txt, .md
                </span>
              </div>

              {error && (
                <div className="deck-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </motion.div>
          )}

          {/* Step 2: Preview */}
          {currentStep === STEPS.PREVIEW && fileData && (
            <motion.div
              key="preview"
              className="deck-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="deck-step-header">
                <FileText size={32} />
                <h2>Vista previa</h2>
                <p>Revisa el contenido antes de generar la presentación</p>
              </div>

              <div className="deck-preview-card">
                <div className="deck-preview-header">
                  <div className="deck-file-info">
                    <FileText size={20} />
                    <span>{fileData.name}</span>
                  </div>
                  <button className="deck-change-file" onClick={handleReset}>
                    <X size={16} />
                    Cambiar
                  </button>
                </div>

                <div className="deck-stats">
                  <div className="deck-stat">
                    <span className="deck-stat-value">{fileData.stats?.lines || 0}</span>
                    <span className="deck-stat-label">Líneas</span>
                  </div>
                  <div className="deck-stat">
                    <span className="deck-stat-value">{fileData.stats?.words || 0}</span>
                    <span className="deck-stat-label">Palabras</span>
                  </div>
                  <div className="deck-stat">
                    <span className="deck-stat-value">{fileData.stats?.characters || 0}</span>
                    <span className="deck-stat-label">Caracteres</span>
                  </div>
                </div>

                <div className="deck-title-input">
                  <label>Título de la presentación</label>
                  <input
                    type="text"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder="Escribe un título..."
                  />
                </div>

                <div className="deck-content-preview">
                  <label>Contenido</label>
                  <pre>{fileData.preview}</pre>
                </div>
              </div>

              {error && (
                <div className="deck-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <button className="deck-generate-btn" onClick={handleGenerate}>
                <Sparkles size={18} />
                Generar presentación
              </button>
            </motion.div>
          )}

          {/* Step 3: Generating */}
          {currentStep === STEPS.GENERATING && (
            <motion.div
              key="generating"
              className="deck-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="deck-generating">
                <div className="deck-generating-animation">
                  <Loader2 size={48} className="deck-spinner" />
                </div>
                <h2>Generando tu presentación</h2>
                <p>Gamma está creando las diapositivas. Esto puede tomar unos segundos...</p>
                
                <div className="deck-generating-steps">
                  <div className="deck-gen-step completed">
                    <CheckCircle2 size={16} />
                    <span>Contenido procesado</span>
                  </div>
                  <div className="deck-gen-step active">
                    <Loader2 size={16} className="deck-mini-spinner" />
                    <span>Generando diapositivas...</span>
                  </div>
                  <div className="deck-gen-step">
                    <span className="deck-gen-dot" />
                    <span>Aplicando diseño</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Complete */}
          {currentStep === STEPS.COMPLETE && (
            <motion.div
              key="complete"
              className="deck-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="deck-complete">
                <div className="deck-success-icon">
                  <CheckCircle2 size={48} />
                </div>
                <h2>Presentación lista</h2>
                <p>Tu presentación ha sido creada exitosamente en Gamma</p>

                {credits && (
                  <div className="deck-credits-info">
                    <span>Créditos usados: {credits.deducted}</span>
                    <span>Créditos restantes: {credits.remaining}</span>
                  </div>
                )}

                <a 
                  href={gammaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="deck-open-gamma"
                >
                  <Presentation size={20} />
                  Abrir en Gamma
                  <ExternalLink size={16} />
                </a>

                <button className="deck-new-btn" onClick={handleReset}>
                  Crear otra presentación
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
